import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { CHART_COLORS, getAgChartTheme, formatPercent } from '../../analytics/charts/chartTheme';
import { useTheme } from '../../../stores/ThemeContext';

const DisciplineTracker = ({ data = [], height = 300 }) => {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) setDimensions({ width, height });
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [height]);

  const chartData = useMemo(() => {
    return data.map(d => ({
      ...d,
      weekLabel: new Date(d.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    }));
  }, [data]);

  const options = useMemo(() => ({
    theme: getAgChartTheme(isDark),
    width: dimensions.width || undefined,
    height: dimensions.height,
    data: chartData,
    series: [
      {
        type: 'line',
        xKey: 'weekLabel',
        yKey: 'discipline_score',
        yName: 'Discipline Score',
        stroke: CHART_COLORS.primary,
        marker: {
          fill: CHART_COLORS.primary,
          stroke: CHART_COLORS.primary,
          size: 5,
        },
        tooltip: {
          renderer: ({ datum }) => ({
            title: datum.weekLabel,
            content: `Score: ${datum.discipline_score}%\nTrades: ${datum.trade_count}`,
          }),
        },
      },
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        label: {
          rotation: -45,
          color: isDark ? '#a1a1aa' : '#6b7280',
          fontSize: 10,
        },
      },
      {
        type: 'number',
        position: 'left',
        min: 0,
        max: 100,
        label: {
          color: isDark ? '#a1a1aa' : '#6b7280',
          formatter: ({ value }) => `${value}%`,
        },
      },
    ],
  }), [chartData, dimensions, isDark]);

  if (data.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-sm font-semibold text-foreground mb-4">Discipline Score Tracker</h3>
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
          No discipline data yet. Journal trades to track your discipline score.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <h3 className="text-sm font-semibold text-foreground mb-4">Discipline Score Tracker</h3>
      <div ref={containerRef} className="w-full">
        {dimensions.width > 0 && <AgCharts options={options} />}
      </div>
    </div>
  );
};

export default DisciplineTracker;
